import { useRef, useLayoutEffect, useState, memo, MouseEvent } from 'react';
import { css } from '@emotion/react';
import { Icon as DSIcon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, SpinningLoader, Text, useTooltip, useModalControl } from '@frontend/design-system';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { HideConversationConfirmationModal } from './hide-conversation-confirmation-modal';

type Props = {
  isSelected: boolean;
  onClick: () => void;
  trackingId: string;
  label: string;
  badgeCount: number;
  Icon: React.ReactNode;
  isLoading?: boolean;
  shouldShowHideButton?: boolean;
  onClickHideConversation?: (e?: MouseEvent<HTMLButtonElement>) => Promise<void>;
  hideButtonTrackingId?: string;
};
export const NavItem = memo(
  ({
    isSelected,
    onClick,
    trackingId,
    label,
    badgeCount,
    Icon,
    isLoading,
    shouldShowHideButton,
    onClickHideConversation,
    hideButtonTrackingId,
  }: Props) => {
    const { Tooltip, tooltipProps, triggerProps, setOpen, isOpen } = useTooltip({ hoverDelay: 300 });
    const chatNameRef = useRef<HTMLElement | null>(null);
    const [offsetWidth, setOffsetWidth] = useState<number>(chatNameRef.current?.offsetWidth ?? 0);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const { modalProps, triggerProps: modalTriggerProps } = useModalControl();
    const skipConversationModal = useTeamChatSelector((ctx) => ctx.hideConversationModal);

    // FIXME: @MehulWeave Refactor this code,
    // ticket->linear.app/getweave/issue/BIZX-1366/refactor-navitem-code-to-optimize-the-ref-and-remove-uselayouteffect
    useLayoutEffect(() => {
      if (!chatNameRef.current) return;
      // need to trigger a rerender to get the width of the element after it has been rendered
      // otherwise the width will be 0 until a rerender is triggered
      setOffsetWidth(chatNameRef?.current?.offsetWidth);
    }, [chatNameRef?.current?.offsetWidth]);

    function chatNameMeasureRef(node: HTMLSpanElement) {
      if (node === null) return;

      chatNameRef.current = node;
      if (!offsetWidth) return;
      if (offsetWidth < node.scrollWidth) {
        setShowTooltip(true);
      } else {
        setShowTooltip(false);
      }
    }

    return (
      <div
        css={[containerStyle, isSelected && selectedContainerStyle]}
        onClick={onClick}
        onMouseOut={() => showTooltip && isOpen && setOpen(false)}
        data-trackingid={trackingId}
        {...(showTooltip && triggerProps)}
      >
        {Icon}
        <Text css={css(chatNameStyle)} ref={chatNameMeasureRef} {...(badgeCount && { weight: 'bold' })}>
          {label}
        </Text>
        {/* Want to avoid unnecessary re-rendering hence adding a check to conditionally render the tooltip */}
        {showTooltip && (
          <Tooltip {...tooltipProps}>
            <Text color='white'>{label}</Text>
          </Tooltip>
        )}
        {isLoading && <SpinningLoader className='loading-status' color='subdued' size='xs' css={marginLeftAuto} />}
        {!isLoading && shouldShowHideButton && (
          <Button
            hoverLabel='Hide Chat'
            variant='secondary'
            className='hideChatButton'
            trackingId={hideButtonTrackingId}
            {...modalTriggerProps}
            {...(skipConversationModal && {
              onClick: onClickHideConversation,
            })}
          >
            <DSIcon name='x-small' size={16} />
          </Button>
        )}
        {!isLoading && !!badgeCount && (
          <Text
            weight='semibold'
            size='medium'
            as='span'
            color='white'
            css={[unreadTextStyle, badgeCount > 9 && paddingForUnread]}
            {...(shouldShowHideButton && { className: 'badge' })}
          >
            {badgeCount}
          </Text>
        )}
        {shouldShowHideButton && onClickHideConversation && (
          <HideConversationConfirmationModal
            modalProps={modalProps}
            onClickHideConversation={onClickHideConversation}
          />
        )}
      </div>
    );
  }
);
NavItem.displayName = 'NavItem';

const containerStyle = css({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5),
  width: '100%',
  background: 'none',
  border: 'none',
  gap: theme.spacing(1),
  cursor: 'pointer',
  marginBottom: '2px',
  transition: 'background-color 0.2s',

  '.hideChatButton': {
    display: 'none',
    padding: 0,
    height: 'unset',
    border: 'none',
    marginLeft: 'auto',
  },

  '&:hover': {
    transition: 'background-color 0.2s',
    backgroundColor: theme.colors.neutral5,
    borderRadius: theme.borderRadius.small,

    '.online-indicator': {
      borderColor: `${theme.colors.neutral5}`,
    },
    '.badge': {
      display: 'none',
    },
    '.hideChatButton': {
      display: 'inline-flex',
    },
  },
});

const selectedContainerStyle = css({
  backgroundColor: theme.colors.neutral5,
  borderRadius: theme.borderRadius.small,
  fontWeight: theme.font.weight.bold,
});

const unreadTextStyle = css({
  lineHeight: 1.6,
  backgroundColor: theme.colors.critical50,
  minWidth: '18px',
  width: 'fit-content',
  height: '18px',
  borderRadius: '9px',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  fontSize: theme.fontSize(12),
  // added to right shift the component in the row
  marginLeft: 'auto',
});

const marginLeftAuto = css({
  marginLeft: 'auto',
});

const paddingForUnread = css({
  padding: theme.spacing(0, 0.75),
});

const chatNameStyle = css({ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' });
